import React, { useRef, useState, useEffect } from 'react';
import { useForm, singleChargeSubmit } from './provider';
import { ArrowLeftIcon, BanknotesIcon, DocumentTextIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { prettyDate } from "../../utils/formatters";

export const OfflinePaymentPage = (props) => {
  const { state, dispatch, chargeData, stripe, elements, setInvoices } = useForm();
  const { invoices, fund, notes, loading, errors } = state;
  const currentChargeData = chargeData();
  
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const resp = await singleChargeSubmit({ dispatch, state, chargeData, stripe, elements });

    if (resp.success) {
      props.goToNamedStep('success_page');
    }
    if (resp.error) {
      dispatch({ type: "SET_ERRORS", errors: { base: resp.error.response.data.error } });
    }
  };

  const handleBackClick = () => {
    dispatch({ type: "SET_OFFLINE_PAYMENT", offlinePayment: false });
    props.goToNamedStep("pick_payment_method");
  };

  const handleNotesChange = (e) => {
    dispatch({ type: "SET_NOTES", notes: e.target.value });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={handleBackClick}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Record Offline Payment</h2>
          <p className="text-sm text-gray-500">Check, cash, or money order</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Invoice Selection */}
        <div className="mb-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Select Invoices</p>
          <div className="space-y-2">
            {invoices.map((invoice) => (
              <label
                key={invoice.id}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  invoice.checked
                    ? 'border-amber-300 bg-amber-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={invoice.checked}
                  onChange={() => setInvoices(invoice.id)}
                  className="h-5 w-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${invoice.checked ? 'text-amber-900' : 'text-gray-900'}`}>
                    {invoice.title}
                  </p>
                  <p className={`text-xs ${invoice.checked ? 'text-amber-600' : 'text-gray-500'}`}>
                    Due: {prettyDate(invoice.due_date)}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-semibold ${invoice.checked ? 'text-amber-700' : 'text-gray-700'}`}>
                    ${(invoice.amount_due_cents / 100).toFixed(2)}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Payment Details */}
        <div className="mb-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Payment Details</p>

          <div className="space-y-4">
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Reference / Check Number
              </label>
              <input
                id="notes"
                type="text"
                value={notes}
                onChange={handleNotesChange}
                placeholder="e.g., Check #1234, Cash, Money Order"
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-300 focus:ring-2 focus:ring-amber-100 transition-colors"
              />
              <p className="mt-1 text-xs text-gray-500">
                Add any reference number or notes about this payment
              </p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-6 bg-amber-50 rounded-xl p-4 border border-amber-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <BanknotesIcon className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-amber-900">Amount to Record</p>
                <p className="text-xs text-amber-700">No processing fees</p>
              </div>
            </div>
            <p className="text-2xl font-bold text-amber-700">
              ${currentChargeData.amount > 0 ? currentChargeData.amount.toFixed(2) : '0.00'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {errors && errors.base && (
          <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
            <p className="text-sm text-red-700">{errors.base}</p>
          </div>
        )}

        {/* Info Notice */}
        <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
          <div className="flex items-start gap-3">
            <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-700">
                Recording an offline payment will mark the selected invoices as paid without processing a card transaction.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div ref={sentinelRef} />
        {isSticky && <div className="h-[60px]" />}
        
        {isSticky ? (
          <div className={`fixed bottom-0 left-0 right-0 ${state.has_sidebar ? 'md:left-64' : ''} p-4 bg-white border-t border-gray-200 shadow-lg z-50`}>
            <div className="max-w-xl mx-auto px-4">
              <button
                type="submit"
                disabled={loading || currentChargeData.amount < 1}
                className={`w-full py-4 px-6 rounded-xl text-white font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                  loading || currentChargeData.amount < 1
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/25'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Recording...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-5 h-5" />
                    <span>Record Payment</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="submit"
            disabled={loading || currentChargeData.amount < 1}
            className={`w-full py-4 px-6 rounded-xl text-white font-semibold text-lg transition-all duration-200 flex items-center justify-center gap-2 ${
              loading || currentChargeData.amount < 1
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-lg shadow-amber-500/25'
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Recording...</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                <span>Record Payment</span>
              </>
            )}
          </button>
        )}
      </form>
    </div>
  );
};
